import React, { useEffect, useState } from "react";
import axios from "axios";
import { Box } from "@mui/material";
import ListColumn from "../../../components/Globo/ListColumn/ListColumn";
import { getProfileUsers } from "../../../services/api/maintenance/permissionCatalog.api";

const columnBorderSx = { borderRight: "1px solid #E2E2E3" };

// Visão "Recurso → Perfil → Usuário": audita quem tem acesso a um recurso e por quê.
// Somente leitura - o catálogo de recursos/perfis já vem pré-carregado por usePermissionCatalog.
const ResourceProfileUserView = ({ resources, profilesByName }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedResourceName, setSelectedResourceName] = useState(null);
  const [selectedProfileName, setSelectedProfileName] = useState(null);
  const [profileUserEmails, setProfileUserEmails] = useState([]);
  const [loadingProfileUsers, setLoadingProfileUsers] = useState(false);

  useEffect(() => {
    if (!selectedProfileName) {
      setProfileUserEmails([]);
      return undefined;
    }

    const source = axios.CancelToken.source();
    setLoadingProfileUsers(true);

    getProfileUsers(selectedProfileName, { cancelToken: source.token })
      .then((emails) => setProfileUserEmails(emails ?? []))
      .catch((error) => {
        if (axios.isCancel(error)) return;
        console.error("Erro ao buscar usuários do perfil:", error);
        setProfileUserEmails([]);
      })
      .finally(() => setLoadingProfileUsers(false));

    return () => source.cancel();
  }, [selectedProfileName]);

  const handleSelectResource = (resourceName) => {
    setSelectedResourceName(resourceName);
    setSelectedProfileName(null);
  };

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const filteredResources = normalizedSearchTerm
    ? resources.filter((resource) =>
        resource.name.toLowerCase().includes(normalizedSearchTerm)
      )
    : resources;

  const resourceItems = filteredResources.map((resource) => ({
    id: resource.name,
    label: resource.name,
  }));

  const selectedResource = selectedResourceName
    ? resources.find((resource) => resource.name === selectedResourceName)
    : null;

  const profileItems = (selectedResource?.profiles ?? []).map((name) => {
    const profile = profilesByName.get(name);
    return {
      id: name,
      label: name,
      active: profile ? profile.isActive : undefined,
    };
  });

  const userItems = profileUserEmails.map((email) => ({
    id: email,
    label: email,
  }));

  return (
    <Box
      sx={{
        display: "flex",
        border: "1px solid #E2E2E3",
        borderRadius: "8px",
        height: "100%",
      }}
    >
      <ListColumn
        title="Recursos"
        items={resourceItems}
        selectedId={selectedResourceName}
        onSelect={handleSelectResource}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Buscar recurso"
        emptyMessage="Nenhum recurso encontrado."
        sx={{ flex: 1, ...columnBorderSx }}
      />

      <ListColumn
        title="Perfis"
        items={profileItems}
        selectedId={selectedProfileName}
        onSelect={setSelectedProfileName}
        searchable={false}
        emptyMessage={
          selectedResourceName
            ? "Nenhum perfil concede este recurso."
            : "Selecione um recurso."
        }
        sx={{ flex: 1, ...columnBorderSx }}
      />

      <ListColumn
        title="Usuários"
        items={userItems}
        selectedId={null}
        searchable={false}
        loading={loadingProfileUsers}
        emptyMessage={
          selectedProfileName
            ? "Este perfil não possui usuários associados."
            : "Selecione um perfil."
        }
        sortAlphabetically={false}
        sx={{ flex: 1 }}
      />
    </Box>
  );
};

export default ResourceProfileUserView;
